/**
 * Every backtest kept: each strategy tested, with its verdict at a glance.
 *
 * A strategy is backtested from its own page by the platform's backtester,
 * which keeps the result here; older ones were kept from the command line
 * (`backtest --keep`, `import-backtest`). The column to read is the edge:
 * survivor-only history flatters every return, so what a strategy earned
 * beyond random picks under its own rules is the figure that survives the
 * flattery.
 */

import { fetchBacktests } from "@/api/client";
import type { BacktestSummary } from "@/api/client";
import { type Column, DataTable } from "@/components/DataTable";
import { Empty } from "@/components/Empty";
import { Failed } from "@/components/Failed";
import { PageHeader } from "@/components/PageHeader";
import { ReadingGuide } from "@/components/ReadingGuide";
import { useResource } from "@/hooks/useResource";
import { byBalance } from "@/lib/balance";
import { BACKTEST_TERMS } from "@/lib/strategyTerms";
import { formatDay, formatDayInIndia } from "@/lib/format";
import { percent, points, ratio } from "@/lib/backtestFigures";
import { backtestPath } from "@/lib/paths";

const COLUMNS: Column<BacktestSummary>[] = [
  {
    id: "name",
    header: "Strategy",
    accessorFn: (row) => row.name,
    cell: ({ row }) => <span className="font-medium">{row.original.name}</span>,
  },
  {
    id: "stretch",
    header: "Traded",
    accessorFn: (row) => row.first_session,
    cell: ({ row }) =>
      `${formatDay(row.original.first_session)} - ${formatDay(row.original.last_session)}`,
    meta: { hint: BACKTEST_TERMS.traded },
  },
  {
    id: "cagr",
    header: "CAGR",
    accessorFn: (row) => row.cagr,
    cell: ({ row }) => percent(row.original.cagr),
    meta: { hint: BACKTEST_TERMS.cagr, align: "right" },
  },
  {
    id: "out_of_sample_cagr",
    header: "Out of sample",
    accessorFn: (row) => row.out_of_sample_cagr,
    cell: ({ row }) => percent(row.original.out_of_sample_cagr),
    meta: { hint: BACKTEST_TERMS.outOfSample, align: "right" },
  },
  {
    id: "out_of_sample_edge",
    header: "Edge vs random",
    accessorFn: (row) => row.out_of_sample_edge,
    cell: ({ row }) => points(row.original.out_of_sample_edge),
    meta: { hint: BACKTEST_TERMS.edge, align: "right", emphasis: true },
  },
  {
    id: "benchmark_lead",
    header: "vs index",
    accessorFn: (row) => row.benchmark_lead,
    cell: ({ row }) => points(row.original.benchmark_lead),
    meta: { hint: BACKTEST_TERMS.benchmarkLead, align: "right" },
  },
  {
    id: "balance",
    header: "Balance",
    accessorFn: (row) => row.balance,
    cell: ({ row }) => <span className="tabular">{ratio(row.original.balance)}</span>,
    meta: { hint: BACKTEST_TERMS.balance, align: "right" },
  },
  {
    id: "max_drawdown",
    header: "Max drawdown",
    accessorFn: (row) => row.max_drawdown,
    cell: ({ row }) => percent(row.original.max_drawdown),
    meta: { hint: BACKTEST_TERMS.maxDrawdown, align: "right" },
  },
  {
    id: "sharpe",
    header: "Sharpe",
    accessorFn: (row) => row.sharpe,
    cell: ({ row }) => ratio(row.original.sharpe),
    meta: { hint: BACKTEST_TERMS.sharpe, align: "right" },
  },
  {
    id: "run_at",
    header: "Run",
    accessorFn: (row) => row.run_at,
    cell: ({ row }) => formatDayInIndia(row.original.run_at),
    meta: { hint: BACKTEST_TERMS.ran },
  },
];

/**
 * Render the page.
 *
 * @returns The page.
 */
export function Backtests(): React.JSX.Element {
  const backtests = useResource(fetchBacktests);

  if (backtests.error !== null) {
    return <Failed message={backtests.error} />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Backtests"
        count={backtests.data === null ? undefined : `${String(backtests.data.length)} backtests`}
        description="Every strategy tested on the stored history and kept, judged in and out of sample, against its index, and against random picks under the same rules, best balanced first: the CAGR per point of the deepest fall, in whichever period was weaker. History before September 2026 holds only the companies that survived, so every return is optimistic: the edge over random picks is the figure to trust."
      />
      <ReadingGuide title="Which figures to read">
        <p>
          <strong>Each backtest runs twice from cash</strong>: on the years to 2017, where its
          settings may have been chosen, and on the years from 2018, which they were not tuned on.
          The second is the test.
        </p>
        <p>
          <strong>The edge over random picks is the figure to trust.</strong> The history holds only
          companies that survived, so even companies picked at random under the same rules earned
          well; the edge is what the rules added beyond that.
        </p>
        <p>
          <strong>Balance orders the list:</strong> return for each point of the deepest fall, in
          whichever period was weaker. Click any column to order by it instead.
        </p>
      </ReadingGuide>
      {backtests.data?.length === 0 ? (
        <Empty
          title="No backtests kept yet"
          reason="Backtest a strategy from its page on Strategies, and the result is kept here."
        />
      ) : (
        <DataTable
          columns={COLUMNS}
          rows={byBalance(backtests.data ?? [], (row) => row.balance)}
          loading={backtests.loading}
          linkTo={(row) => backtestPath(row.backtest_id)}
          label="Kept backtests"
        />
      )}
    </div>
  );
}
